import React from 'react';

import { useUser } from '@clerk/nextjs';
import { captureException } from '@sentry/nextjs';
import { useRouter } from 'next/router';

import { trpc } from '@/utils/trpc';

export const useEpixelClick = () => {
  const [isSSOLoading, setIsSSOLoading] = React.useState(false);

  const { user, isSignedIn } = useUser();
  const userId = user?.externalId;
  const { data: dbUser } = trpc.users.byId.useQuery(
    { id: userId || '' },
    { enabled: !!userId, refetchInterval: isSSOLoading ? 1000 : false },
  );

  const { mutateAsync: beginSSO } = trpc.epixel.beginSSO.useMutation();
  const { mutateAsync: getUserDetails } = trpc.epixel.getUserDetails.useMutation();
  const { mutateAsync: createUser } = trpc.epixel.create.useMutation();

  const userHasMLMAccount = !!dbUser?.data?.epixelId;

  const isSubOrAdmin = dbUser?.data?.isSubscriber || dbUser?.data?.isAdmin;
  const router = useRouter();

  const handleViewAffiliate = async () => {
    //? This should kick off the SSO process and route the user to the BE
    if (!isSSOLoading) setIsSSOLoading(true);
    const { url } = await beginSSO();
    window.open(url, '_blank');
  };

  const handleBecomeAffiliate = async () => {
    if (!userId) {
      router.push('signup/questionnaire');
      return;
    }
    setIsSSOLoading(true);
    const epixelUserResult = await getUserDetails({ id: userId });
    if (epixelUserResult.success) {
      // User has and account, we just need to update our DB here
      await handleViewAffiliate();
    } else {
      const result = await createUser({ id: userId });
      if (result.success) {
        await handleViewAffiliate();
      } else {
        captureException(new Error('Failed to create user'), {
          extra: { result },
        });
      }
      setIsSSOLoading(false);
    }
  };

  let buttonFunction: () => void = handleBecomeAffiliate;

  if (isSSOLoading) {
    buttonFunction = () => null;
  } else if (!isSignedIn) {
    //? If they aren't signed in, they need to sign up or login
    buttonFunction = () => router.push('/signup/questionnaire?source=collective&step=0');
  } else if (!isSubOrAdmin) {
    //? If they are, but have no sub they need one
    buttonFunction = () => router.push('/signup/collective/activate');
  } else if (userHasMLMAccount) {
    //? If the have an MLM account already, lets send them to the portal
    buttonFunction = handleViewAffiliate;
  } else {
    //? They can create an account
    buttonFunction = handleBecomeAffiliate;
  }

  return {
    buttonFunction,
    isLoading: isSSOLoading,
    hasJoined: !!userHasMLMAccount,
  };
};
